import { useEffect, useCallback } from 'react';
import type {
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  FieldValues,
  Path,
} from 'react-hook-form';
import type { IntakeResponse } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import {
  formatPhone,
  formatSSN,
  formatZipCode,
  normalizeGender,
} from '../../utils/intake';
import { createFuzzyMatcher } from '../../utils/fuzzyMatch';
import { LANGUAGE_OPTIONS, OTHER_LANGUAGE_KEY } from './constants';
import type { CreatePatientFormData } from './types';

// Helper to get default SOC/episode date (today + 2 days) in YYYY-MM-DD format (Pacific Time)
const getDefaultSocDate = (): string => {
  const now = new Date();
  // Get current date in Pacific Time
  const ptDate = new Date(
    now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
  );
  ptDate.setDate(ptDate.getDate() + 2);

  const year = ptDate.getFullYear();
  const month = String(ptDate.getMonth() + 1).padStart(2, '0');
  const day = String(ptDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface UseFormInitializationOptions<TFieldValues extends FieldValues> {
  selectedIntake: IntakeRecord | null;
  reset: UseFormReset<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  saveToServer: (updates: Partial<TFieldValues>) => Promise<void>;
}

/**
 * Hook for initializing and resetting the form with intake data.
 * Handles form population, formatting, and language fuzzy matching.
 * Generic to work with any form schema that is a subset of CreatePatientFormData.
 */
export function useFormInitialization<TFieldValues extends FieldValues>({
  selectedIntake,
  reset,
  setValue,
  trigger,
  saveToServer,
}: UseFormInitializationOptions<TFieldValues>): void {
  // Helper to safely set a value only if the field exists in the form schema
  const safeSetValue = useCallback(
    (fieldName: string, value: unknown) => {
      try {
        // Cast value to the expected type for setValue
        setValue(
          fieldName as Path<TFieldValues>,
          value as TFieldValues[Path<TFieldValues>]
        );
      } catch {
        // Field doesn't exist in this form schema, skip it
      }
    },
    [setValue]
  );

  // Helper to build form values with only fields that exist in the target schema
  const buildFormValues = useCallback(
    (
      intake: IntakeResponse,
      formattedPatientFields: Record<string, unknown>
    ) => {
      // Build the full set of possible values
      const allValues: Partial<CreatePatientFormData> = {
        // Kinnser Information
        kinnser_clinic_id:
          intake.agency_clinic?.clinic_key || intake.kinnser_clinic_id || null,
        kinnser_physician_id: intake.kinnser_physician_id || null,
        kinnser_branch_id: intake.kinnser_branch_id || null,
        kinnser_insurance_provider_id:
          intake.agency_insurance?.insurance_key ||
          intake.kinnser_insurance_provider_id ||
          null,
        kinnser_admission_type: intake.kinnser_admission_type || null,
        kinnser_hcpcs_code_key: intake.kinnser_hcpcs_code_key || null,
        kinnser_mrn: intake.kinnser_mrn || null,
        kinnser_hh_cahps_opt_out: intake.kinnser_hh_cahps_opt_out || null,
        kinnser_language_key:
          (intake as IntakeResponse & { kinnser_language_key?: string })
            .kinnser_language_key || null,
        kinnser_other_language:
          (intake as IntakeResponse & { kinnser_other_language?: string })
            .kinnser_other_language || null,

        // Kinnser Episode fields
        kinnser_create_episode: intake.kinnser_create_episode || null,
        kinnser_type_of_soc: intake.kinnser_type_of_soc || null,
        kinnser_soc_date: intake.kinnser_soc_date,
        kinnser_episode_start_date: intake.kinnser_episode_start_date,
        kinnser_clinician_id: intake.kinnser_clinician_id || null,
        kinnser_clinical_care_manager_id:
          intake.kinnser_clinical_care_manager_id || null,
        kinnser_case_manager_id: intake.kinnser_case_manager_id || null,
        kinnser_oasis_approver_id: intake.kinnser_oasis_approver_id || null,
        kinnser_pt_approver_id: intake.kinnser_pt_approver_id || null,
        kinnser_ot_approver_id: intake.kinnser_ot_approver_id || null,
        kinnser_st_approver_id: intake.kinnser_st_approver_id || null,

        // Roger Episode fields
        roger_create_episode: intake.roger_create_episode || null,
        roger_type_of_soc: intake.roger_type_of_soc || null,

        // Push to EMR flags
        push_to_roger: intake.push_to_roger || null,
        push_to_kinnser: intake.push_to_kinnser || null,

        // Patient Identification
        patient_first_name: intake.patient_first_name || null,
        patient_middle_name: intake.patient_middle_name || null,
        patient_last_name: intake.patient_last_name || null,
        patient_dob: intake.patient_dob || null,
        // Normalize gender to M/F/U (handles legacy Male/Female/Unknown values)
        patient_gender: normalizeGender(intake.patient_gender),
        patient_ssn: intake.patient_ssn || null,
        patient_primary_language: intake.patient_primary_language || null,

        // Contact Information
        patient_phone: intake.patient_phone || null,
        patient_email: intake.patient_email || null,
        patient_street_address: intake.patient_street_address || null,
        patient_city: intake.patient_city || null,
        patient_state: intake.patient_state || null,
        patient_zip_code: intake.patient_zip_code || null,
        patient_county: intake.patient_county || null,

        // Insurance Information
        medicaid_id: (intake.medicaid_id as string | null | undefined) || null,

        // Notes
        patient_notes: intake.patient_notes || null,

        // Auto-formatted fields (SSN, ZIP, phone)
        ...formattedPatientFields,
      };

      // Filter to only include fields that can be set in the target form
      const filteredValues: Partial<TFieldValues> = {};
      for (const [key, value] of Object.entries(allValues)) {
        try {
          // Attempt to create a path - this will fail if the field doesn't exist
          filteredValues[key as keyof TFieldValues] =
            value as TFieldValues[keyof TFieldValues];
        } catch {
          // Field doesn't exist in target schema, skip it
        }
      }

      return filteredValues;
    },
    []
  );

  // Get formatted patient fields helper
  const getFormattedPatientFields = useCallback(() => {
    if (!selectedIntake) return {};
    const intake = selectedIntake as unknown as IntakeResponse;

    const formattedSSN = intake.patient_ssn
      ? formatSSN(intake.patient_ssn)
      : null;
    const formattedZip = intake.patient_zip_code
      ? formatZipCode(intake.patient_zip_code)
      : null;
    const formattedPhone = intake.patient_phone
      ? formatPhone(intake.patient_phone)
      : null;
    const formattedEmergencyPhone = intake.emergency_contact_phone
      ? formatPhone(intake.emergency_contact_phone)
      : null;
    const formattedReferringPhone = intake.referring_phone
      ? formatPhone(intake.referring_phone)
      : null;
    const formattedPoaPhone = intake.poa_phone
      ? formatPhone(intake.poa_phone)
      : null;
    const formattedSurrogatePhone = intake.surrogate_phone
      ? formatPhone(intake.surrogate_phone)
      : null;

    // Return only fields that changed due to formatting
    const formattedValues: Record<string, string | null> = {};
    if (formattedSSN !== intake.patient_ssn && intake.patient_ssn)
      formattedValues.patient_ssn = formattedSSN;
    if (formattedZip !== intake.patient_zip_code && intake.patient_zip_code)
      formattedValues.patient_zip_code = formattedZip;
    if (formattedPhone !== intake.patient_phone && intake.patient_phone)
      formattedValues.patient_phone = formattedPhone;
    if (
      formattedEmergencyPhone !== intake.emergency_contact_phone &&
      intake.emergency_contact_phone
    )
      formattedValues.emergency_contact_phone = formattedEmergencyPhone;
    if (
      formattedReferringPhone !== intake.referring_phone &&
      intake.referring_phone
    )
      formattedValues.referring_phone = formattedReferringPhone;
    if (formattedPoaPhone !== intake.poa_phone && intake.poa_phone)
      formattedValues.poa_phone = formattedPoaPhone;
    if (
      formattedSurrogatePhone !== intake.surrogate_phone &&
      intake.surrogate_phone
    )
      formattedValues.surrogate_phone = formattedSurrogatePhone;

    return formattedValues;
  }, [selectedIntake]);

  // Update form data when intake is loaded or changes
  useEffect(() => {
    // Skip if no intake data
    if (!selectedIntake) return;

    // Cast to IntakeResponse since IntakeRecord is a subset of IntakeResponse
    const intake = selectedIntake as unknown as IntakeResponse;

    const formattedPatientFields = getFormattedPatientFields();

    // Check for auto-filled SOC date and episode start date
    const autoFilledFields: Partial<CreatePatientFormData> = {};
    if (!intake.kinnser_soc_date) {
      autoFilledFields.kinnser_soc_date = getDefaultSocDate();
    }
    if (!intake.kinnser_episode_start_date) {
      autoFilledFields.kinnser_episode_start_date = getDefaultSocDate();
    }

    // Combine all server updates
    const serverUpdates = { ...formattedPatientFields, ...autoFilledFields };

    if (Object.keys(serverUpdates).length > 0) {
      saveToServer(serverUpdates as Partial<TFieldValues>);
    }

    // Build form values filtered to only fields that exist in the target schema
    const formValues = buildFormValues(intake, formattedPatientFields);

    reset(formValues as TFieldValues);

    // Trigger validation after form is populated
    setTimeout(() => trigger(), 0);

    // Run fuzzy matching for language only if there's meaningful language data
    // Only applicable if the form has language fields
    const primaryLanguage = intake?.patient_primary_language?.trim();
    const languageUpdates: Record<string, string | null> = {};

    // Check if this form schema includes language fields before processing
    const hasLanguageFields =
      'kinnser_language_key' in formValues &&
      'kinnser_other_language' in formValues;

    if (hasLanguageFields) {
      if (primaryLanguage && primaryLanguage.length >= 2) {
        const languageMatcher = createFuzzyMatcher(LANGUAGE_OPTIONS, ['label']);
        const languageMatch = languageMatcher.findBestMatch(primaryLanguage);

        if (languageMatch) {
          console.log(
            `Auto-matching language "${primaryLanguage}" to "${languageMatch.label}"`
          );
          safeSetValue('kinnser_language_key', languageMatch.value);
          if (
            !formValues.kinnser_language_key ||
            formValues.kinnser_language_key === null
          ) {
            languageUpdates.kinnser_language_key = languageMatch.value;
          }
        } else {
          // No match found - set to Other/Unknown and use original language value
          console.log(
            `No language match for "${primaryLanguage}" - setting to Other/Unknown`
          );
          if (
            !formValues.kinnser_language_key ||
            formValues.kinnser_language_key === null
          ) {
            languageUpdates.kinnser_language_key = OTHER_LANGUAGE_KEY;
          }
          safeSetValue('kinnser_language_key', OTHER_LANGUAGE_KEY);
          if (
            !formValues.kinnser_other_language ||
            formValues.kinnser_other_language === null
          ) {
            languageUpdates.kinnser_other_language = primaryLanguage;
          }
          safeSetValue('kinnser_other_language', primaryLanguage);
        }
      } else {
        // No language data or insufficient length - set to Other/Unknown without logging
        safeSetValue('kinnser_language_key', OTHER_LANGUAGE_KEY);
        if (
          !formValues.kinnser_language_key ||
          formValues.kinnser_language_key === null
        ) {
          languageUpdates.kinnser_language_key = OTHER_LANGUAGE_KEY;
        }
        if (primaryLanguage) {
          if (
            !formValues.kinnser_other_language ||
            formValues.kinnser_other_language === null
          ) {
            languageUpdates.kinnser_other_language = primaryLanguage;
          }
          safeSetValue('kinnser_other_language', primaryLanguage);
        }
      }

      // Save language auto-matching results to server
      if (Object.keys(languageUpdates).length > 0) {
        console.log(
          'Updating fuzzy matched language key to server',
          languageUpdates
        );
        saveToServer(languageUpdates as Partial<TFieldValues>);
      }

      // Ensure patient_primary_language is consistent with the language dropdown selection
      // This implements the logic where dropdown changes drive patient_primary_language
      // Only process if patient_primary_language exists in this form schema
      if ('patient_primary_language' in formValues) {
        const finalLanguageKey = formValues.kinnser_language_key as
          | string
          | null;
        if (finalLanguageKey && finalLanguageKey !== OTHER_LANGUAGE_KEY) {
          const languageOption = LANGUAGE_OPTIONS.find(
            option => option.value === finalLanguageKey
          );
          safeSetValue(
            'patient_primary_language',
            languageOption?.label || null
          );
        } else if (finalLanguageKey === OTHER_LANGUAGE_KEY) {
          safeSetValue(
            'patient_primary_language',
            (formValues.kinnser_other_language as string | null) || null
          );
        }
      }
    }
  }, [
    selectedIntake,
    reset,
    setValue,
    trigger,
    getFormattedPatientFields,
    saveToServer,
    buildFormValues,
    safeSetValue,
  ]);
}
